import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Download, Loader2, Printer } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { PageHeader, Forbidden, EmptyState, ErrorDisplay } from '@/components'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { downloadCSV } from '@/lib/csv'
import type {
  CompliancePeriod,
  ComplianceReportBuilding,
  ComplianceReportRoom,
  ComplianceStatus,
} from '@/types'
import {
  csvFilename,
  formatLastCleaned,
  formatProgress,
  formatTarget,
  reportToCSV,
  statusLabel,
} from './csvExport'
import { useComplianceReport } from './useComplianceReport'
import './print.css'

const ALL_BUILDINGS = '__all__'

// Stable ids for the ARIA tablist / tabpanel wiring. Kept module-scope
// so every render produces the same strings and the panel's
// aria-labelledby always resolves to a DOM element.
const COMPLIANCE_PANEL_ID = 'compliance-report-panel'
const tabIdFor = (p: CompliancePeriod) => `compliance-tab-${p}`

/**
 * Visual variant class names per ComplianceStatus. Colors match the spec
 * §Compliance page status legend:
 *   compliant     → green
 *   in_progress   → amber
 *   missed        → red solid
 *   never_cleaned → red outline
 *   no_target     → gray
 *   not_this_period → muted
 *
 * Using Tailwind utility classes directly (rather than extending the
 * Badge variants) keeps the compliance colors colocated with this page.
 */
const STATUS_BADGE_CLASSES: Record<ComplianceStatus, string> = {
  compliant:
    'border-transparent bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-100',
  in_progress:
    'border-transparent bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100',
  missed: 'border-transparent bg-red-600 text-white',
  never_cleaned: 'border-red-600 text-red-700 dark:text-red-300 bg-transparent',
  no_target: 'border-transparent bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  not_this_period: 'border-transparent bg-muted text-muted-foreground',
}

function StatusBadge({ status }: { status: ComplianceStatus }) {
  return (
    <Badge data-testid={`status-${status}`} className={cn(STATUS_BADGE_CLASSES[status])}>
      {statusLabel(status)}
    </Badge>
  )
}

/**
 * Parse the period query param, defaulting to 'day' for missing or
 * invalid values. Keeping this in a helper so tests and tab-switch
 * handlers agree on the interpretation.
 */
function parsePeriod(raw: string | null): CompliancePeriod {
  return raw === 'week' ? 'week' : 'day'
}

/**
 * Case-insensitive "contains" match. Used for the room-name search
 * filter so admins can jump to a specific room in a 2000-room org.
 */
function matchesQuery(room: ComplianceReportRoom, q: string): boolean {
  if (!q) return true
  return room.room_name.toLowerCase().includes(q)
}

/**
 * Number of rooms in a building that are in the "compliant" state.
 * Used for the "X of Y rooms compliant" fraction in each group header.
 */
function compliantCount(rooms: ComplianceReportRoom[]): number {
  return rooms.filter((r) => r.status === 'compliant').length
}

/**
 * Format an ISO timestamp for display in the table. Uses the browser's
 * locale; for the CSV export we keep the raw ISO string (see csvExport.ts).
 */
function formatDateTime(iso: string | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

/**
 * Top-level Compliance page. Renders the current-period report with
 * Day / Week tabs, per-building grouped tables, and CSV / print actions.
 *
 * Authorization: admin + super_admin only. Non-admin users get the
 * <Forbidden /> fallback, matching the Wave 1 Timezone precedent.
 *
 * State model:
 *   - `period` is stored in the URL query string so refreshing or
 *     sharing the page preserves the tab selection.
 *   - `buildingFilter` and `search` are component-local; they only
 *     affect rendering, not the fetched report.
 */
export function CompliancePage() {
  const { user } = useAuth()
  const { effectiveOrgId } = useOrg()
  const [searchParams, setSearchParams] = useSearchParams()

  const period = parsePeriod(searchParams.get('period'))
  const reportQuery = useComplianceReport(effectiveOrgId, period)

  const [buildingFilter, setBuildingFilter] = useState<string>(ALL_BUILDINGS)
  const [search, setSearch] = useState('')

  // Filter / reshape the report for rendering. A client-side filter is
  // sufficient because the spec caps the expected payload well below
  // what a browser can render in-memory (§Storage / worst-case size).
  const filteredBuildings = useMemo<ComplianceReportBuilding[]>(() => {
    const report = reportQuery.data
    if (!report) return []
    const q = search.trim().toLowerCase()
    return report.buildings
      .filter((b) => buildingFilter === ALL_BUILDINGS || b.building_id === buildingFilter)
      .map((b) => ({
        ...b,
        rooms: b.rooms.filter((r) => matchesQuery(r, q)),
      }))
      .filter((b) => b.rooms.length > 0 || !q)
  }, [reportQuery.data, buildingFilter, search])

  // Admin gate — Organization Settings uses the same pattern
  // (in-page <Forbidden />) so we mirror it here for the /compliance
  // route. The sidebar already hides this item for non-admins.
  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return <Forbidden />
  }

  function handlePeriodChange(next: CompliancePeriod) {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('period', next)
    setSearchParams(newParams, { replace: true })
  }

  function handleDownloadCSV() {
    const report = reportQuery.data
    if (!report) return
    downloadCSV(reportToCSV({ ...report, buildings: filteredBuildings }), csvFilename(report))
  }

  function handlePrint() {
    window.print()
  }

  return (
    <div className="space-y-6 compliance-print-root">
      <div data-print-hide>
        <PageHeader
          title="Compliance"
          description={
            reportQuery.data
              ? `As of ${formatDateTime(reportQuery.data.generated_at)} · Org timezone: ${reportQuery.data.period.tz_label}`
              : 'Current-period cleaning compliance across your organization.'
          }
          actions={
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleDownloadCSV} disabled={!reportQuery.data}>
                <Download className="mr-2 h-4 w-4" />
                Download CSV
              </Button>
              <Button variant="outline" onClick={handlePrint} disabled={!reportQuery.data}>
                <Printer className="mr-2 h-4 w-4" />
                Print / Save as PDF
              </Button>
            </div>
          }
        />
      </div>

      {/* Period tabs. Two buttons rather than a Radix Tabs primitive —
          Radix Tabs aren't installed and two buttons match the spec's
          Day|Week switcher without pulling in a new dependency.

          ARIA wiring: each tab carries a stable id and aria-controls
          pointing at the single tabpanel below (COMPLIANCE_PANEL_ID).
          The tabpanel's aria-labelledby tracks the currently-selected
          tab so assistive tech reports the right tab name for the
          visible content. */}
      <div
        role="tablist"
        aria-label="Report period"
        className="inline-flex rounded-md border bg-muted p-1"
        data-print-hide
      >
        {(['day', 'week'] as const).map((p) => {
          const selected = period === p
          return (
            <button
              key={p}
              id={tabIdFor(p)}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={COMPLIANCE_PANEL_ID}
              onClick={() => handlePeriodChange(p)}
              className={cn(
                'rounded px-4 py-1.5 text-sm font-medium transition-colors',
                selected
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {p === 'day' ? 'Day' : 'Week'}
            </button>
          )
        })}
      </div>

      <div
        role="tabpanel"
        id={COMPLIANCE_PANEL_ID}
        aria-labelledby={tabIdFor(period)}
        className="space-y-6"
      >
        {/* Filter + search bar. Admin-visible, hidden on print. */}
        {reportQuery.data && (
          <div className="flex flex-wrap items-center gap-3" data-print-hide>
            <div className="flex items-center gap-2">
              <label htmlFor="compliance-building-filter" className="text-sm">
                Building:
              </label>
              <Select value={buildingFilter} onValueChange={setBuildingFilter}>
                <SelectTrigger
                  id="compliance-building-filter"
                  aria-label="Building filter"
                  className="w-64"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_BUILDINGS}>All buildings</SelectItem>
                  {reportQuery.data.buildings.map((b) => (
                    <SelectItem key={b.building_id} value={b.building_id}>
                      {b.building_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Input
              type="search"
              placeholder="Search rooms…"
              aria-label="Search rooms"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
          </div>
        )}

        {/* Body. */}
        {reportQuery.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : reportQuery.isError ? (
          <ErrorDisplay
            status="error"
            title="Failed to load compliance report"
            message={
              reportQuery.error instanceof Error
                ? reportQuery.error.message
                : 'Unexpected error — please try again.'
            }
            onRetry={() => reportQuery.refetch()}
            isRetrying={reportQuery.isFetching}
          />
        ) : !reportQuery.data || reportQuery.data.buildings.length === 0 ? (
          <EmptyState
            title="No buildings yet"
            description="Add buildings and rooms to your organization, then return here to see their compliance status."
          />
        ) : filteredBuildings.length === 0 ? (
          <EmptyState title="No results" description="No rooms match the current filter." />
        ) : (
          <div className="space-y-4">
            {filteredBuildings.map((building) => (
              <BuildingSection key={building.building_id} building={building} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function BuildingSection({ building }: { building: ComplianceReportBuilding }) {
  const compliant = compliantCount(building.rooms)
  const total = building.rooms.length
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-baseline justify-between gap-2 text-lg">
          <span>{building.building_name}</span>
          <span className="text-sm font-normal text-muted-foreground">
            {compliant} of {total} {total === 1 ? 'room' : 'rooms'} compliant
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Room</TableHead>
              <TableHead>Floor</TableHead>
              <TableHead>Target</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last cleaned</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {building.rooms.map((room) => (
              <TableRow key={room.room_id}>
                <TableCell className="font-medium">{room.room_name}</TableCell>
                <TableCell className="text-muted-foreground">{room.floor || '—'}</TableCell>
                <TableCell className="text-muted-foreground">{formatTarget(room) || '—'}</TableCell>
                <TableCell className="text-muted-foreground">
                  {formatProgress(room) || '—'}
                </TableCell>
                <TableCell>
                  <StatusBadge status={room.status} />
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDateTime(formatLastCleaned(room) || undefined)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
